"use client";

import { styled } from "@mui/material/styles";
import ReturnLink from "components/ReturnLink";
import { CurationBreadcrumbs, useBreadcrumbItems } from "containers/Curation/CurationBreadcrumbs";
import { CurationSiblings } from "containers/Curation/CurationSiblings";
import { addToCart, useViewItem } from "containers/GoogleAnalytics/googleAnalyticsEvents";
import { useBreadcrumbListQuery, useCurationContentQuery } from "gql";
import { useConsumerTripInfo } from "hooks/useConsumerTripInfo";
import { useEntityConfigurationValueByKey } from "hooks/useEntityConfiguration";
import { useFavouriteButton } from "hooks/useFavouriteButton";
import { useHeaderConfiguration } from "hooks/useHeaderEntityConfiguration";
import { useIsCurationBreadcrumbsEnabled } from "hooks/useIsCurationBreadcrumbsEnabled";
import { useBookingExternalReference } from "hooks/usePersistedQueryParams";
import { getNewIdSelector } from "utils/consumerTrip/model/types";

import { FadeUpAnimationWrapper } from "@holibob-packages/ui-core/components";
import { ProductFullLoaded } from "@holibob-packages/ui-core/containers";
import { ProductQuery } from "@holibob-packages/ui-core/gql-request";
import { useRouter } from "@holibob-packages/ui-core/navigation";

export type ProductPageProps = {
    product: ProductQuery["product"];
};
export const ProductPage = ({ product }: ProductPageProps) => {
    const router = useRouter();
    const withSectionFadeUpAnimation =
        useEntityConfigurationValueByKey<boolean>("SETTING.SECTION_FADE_UP_ANIMATION") ?? false;
    const withSiblingCurations = useEntityConfigurationValueByKey("SETTING.ENABLED_CURATION_SIBLINGS") ?? false;
    const {
        isAvailable: showFavouriteButton,
        isFavourite,
        setIsFavourite,
    } = useFavouriteButton(product.id, product.isFavourite ?? false);

    useViewItem(product.id, product.name, product.guidePrice, product.guidePriceCurrency);
    const appHeaderConfig = useHeaderConfiguration();
    const { consumerTripId, consumerId } = useConsumerTripInfo();
    const consumerTripBookingInput = {
        selector: getNewIdSelector(consumerTripId),
        consumer: { selector: getNewIdSelector(consumerId) },
    };

    return (
        <>
            <ProductFullLoaded
                product={product}
                belowHeaderElement={<ProductBelowHeaderContent />}
                bookingReference={useBookingExternalReference()}
                onBookingCompleted={({ id: bookingId }) => {
                    addToCart(product.id, product.name, product.guidePrice, product.guidePriceCurrency);
                    router.push(`/booking/${bookingId}`);
                }}
                withStickyAppBar={appHeaderConfig.isSticky}
                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                showFavouriteButton={showFavouriteButton}
                isFavourite={isFavourite}
                onFavouriteChange={setIsFavourite}
                consumerTrip={consumerTripBookingInput}
            >
                {withSiblingCurations && (
                    <ProductCurationSiblingsContainer withSectionFadeUpAnimation={withSectionFadeUpAnimation} />
                )}
            </ProductFullLoaded>
        </>
    );
};

const ProductCurationSiblingsContent = styled("div")(({ theme }) => ({
    margin: theme.spacing(4, 0, 4, 0),
}));

function ProductBelowHeaderContent() {
    const withBreadcrumbs = useIsCurationBreadcrumbsEnabled();

    return (
        <>
            <ReturnLink />
            {withBreadcrumbs && <CurationBreadcrumbs />}
        </>
    );
}

function ProductCurationSiblingsContainer(props: { withSectionFadeUpAnimation?: boolean }) {
    const { withSectionFadeUpAnimation } = props;
    const { items } = useBreadcrumbItems();
    const canDisplaySiblingCurations = items.length > 1;
    const { data: breadcrumbData } = useBreadcrumbListQuery({
        variables: { items },
        skip: !canDisplaySiblingCurations,
    });
    const breadcrumbs = breadcrumbData?.breadcrumbList.nodes ?? [];
    const currentCurationId = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : undefined;

    if (!currentCurationId) {
        return null;
    }

    return (
        <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
            <ProductCurationSiblingsContent>
                <ProductCurationSiblings curationId={currentCurationId.id} curationIdsPath={items} />
                <ReturnLink />
            </ProductCurationSiblingsContent>
        </FadeUpAnimationWrapper>
    );
}

type ProductCurationSiblingsProps = {
    curationId: string;
    curationIdsPath: string[];
};

function ProductCurationSiblings(props: ProductCurationSiblingsProps) {
    const { curationId, curationIdsPath } = props;
    const { data } = useCurationContentQuery({ variables: { curationId } });
    const curationContent = data?.curationContent;

    if (!curationContent) return null;

    const { curation, siblings, curationParent } = curationContent;

    if (!curationParent) return null;

    return (
        <CurationSiblings
            currentCurationId={curation.id}
            curationSiblings={siblings}
            parentName={curationParent.name ?? ""}
            curationIdsPath={curationIdsPath}
        />
    );
}
